import DesignTokens from '@tableau/design-tokens';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { FlyoutListMenuWidget, MenuButtonWidget, Tooltip } from '@tableau/widgets';
import React from 'react';
import { Action } from 'redux';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { applyColor } from '~src/style/ColorService';

import { createNewConnectedApplication, createNewEASConfiguration } from '../actions/ConnectedApplicationActions';

export enum NewConnectedApplicationActionType {
  DIRECT_TRUST = 'DIRECT_TRUST',
  EAS = 'EAS',
}

interface NewConnectedAppAction {
  type: NewConnectedApplicationActionType;
  label: string;
  performAction: () => Action;
  successMessage: string;
  errorMessage: string;
}

function renderTooltipContent(): JSX.Element {
  return (
    <div className={DetailsTooltipStyles.MetadataSection}>
      <div className={DetailsTooltipStyles.MetadataTitle}>{Messages.connectedApplicationsCustomApplication()}</div>
      <div className={DetailsTooltipStyles.MetadataTextDescription}>{Messages.connectedApplicationsDescriptionDirectTrust()}</div>
      <span>&nbsp;&nbsp;</span>
      <div className={DetailsTooltipStyles.MetadataTitle}>{Messages.connectedApplicationsExternalAuthorizationServer()}</div>
      <div className={DetailsTooltipStyles.MetadataTextDescription}>{Messages.connectedApplicationsDescriptionEAS()}</div>
    </div>
  );
}

export function NewConnectedAppMenuButton(): JSX.Element {
  const [isOpen, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const menuId = 'menu-button-id';

  const actions: NewConnectedAppAction[] = [
    {
      type: NewConnectedApplicationActionType.DIRECT_TRUST,
      label: Messages.connectedApplicationsCustomApplication(),
      performAction: createNewConnectedApplication,
      successMessage: Messages.connectedApplicationsConnectedApplicationCreated(),
      errorMessage: Messages.errorUnknown(),
    },
    {
      type: NewConnectedApplicationActionType.EAS,
      label: Messages.connectedApplicationsExternalAuthorizationServer(),
      performAction: createNewEASConfiguration,
      successMessage: Messages.connectedApplicationsConnectedApplicationCreated(),
      errorMessage: Messages.errorUnknown(),
    },
  ];

  function onSelect(value: NewConnectedApplicationActionType) {
    setOpen(false);
    const action = actions.find(x => x.type === value);
    getAppStore().dispatch(action.performAction());
  }

  return <>
    <MenuButtonWidget
      aria-controls={menuId}
      testId='new-connected-applications-menu-button'
      isOpen={isOpen}
      onClick={() => setOpen(!isOpen)}
      ref={setAnchorEl}
      children={Messages.connectedApplicationNewConnectedApplicationButton()}
    />
    {isOpen &&
      <FlyoutListMenuWidget
        anchorEl={anchorEl!}
        allowAutoPosition
        menuItemInfos={actions.map(x => ({ displayValue: x.label, value: x.type }))}
        onSelect={onSelect}
        requestCloseAll={() => setOpen(false)}
        id={menuId}
        menuStyle={{ minWidth: anchorEl!.getBoundingClientRect().width }}
        subMenuAllowAutoPosition
        testId='new-connected-applications-action-list'
        toggleEl={anchorEl!}
      />
    }
    <Tooltip getContent={renderTooltipContent}>
      <span>
        <NotificationInfoOutlineIcon
          size={20}
          alt={Messages.connectedAppInfoTip()}
          color={applyColor(DesignTokens.color.font.default.secondary, '#666666')}
        />
      </span>
    </Tooltip>
  </>;
}